import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

const Comparison = () => {
  const features = [
    { name: 'Cool Appearance', dyed: 'good', ceramic: 'excellent' },
    { name: 'Privacy', dyed: 'good', ceramic: 'excellent' },
    { name: 'Heat Rejection', dyed: 'fair', ceramic: 'excellent' },
    { name: 'Sunglare Minimization', dyed: 'fair', ceramic: 'excellent' },
    { name: 'Interior Protection', dyed: 'average', ceramic: 'excellent' },
    { name: 'Durability', dyed: 'poor', ceramic: 'excellent' },
    { name: 'Visibility', dyed: 'fair', ceramic: 'excellent' }
  ];

  const getRating = (rating) => {
    if (rating === 'excellent') return { icon: Check, color: 'text-green-500', bg: 'bg-green-500/10' };
    if (rating === 'good') return { icon: Check, color: 'text-amber-500', bg: 'bg-amber-500/10' };
    if (rating === 'fair' || rating === 'average') return { icon: Check, color: 'text-orange-500', bg: 'bg-orange-500/10' };
    return { icon: X, color: 'text-red-500', bg: 'bg-red-500/10' };
  };

  return (
    <section id="comparison" className="py-24 bg-black relative overflow-hidden border-t border-gray-900">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold-600/20 rounded-full blur-[120px]"></div>
      </div>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-gold-500 font-semibold text-sm uppercase tracking-wider">Film Comparison</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">See The Difference</h2>
          <p className="text-gray-400 text-lg">
            Compare standard dyed film with premium ceramic technology
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left p-6 text-gray-400 font-semibold">Features</th>
                      <th className="text-center p-6">
                        <div className="text-white font-bold text-lg">Dyed Film</div>
                        <Badge variant="outline" className="mt-2 border-gray-700 text-gray-400">Standard</Badge>
                      </th>
                      <th className="text-center p-6">
                        <div className="text-white font-bold text-lg">Ceramic Film</div>
                        <Badge className="mt-2 bg-gold-500 text-black font-bold">RECOMMENDED</Badge>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {features.map((feature, index) => {
                      const dyedRating = getRating(feature.dyed);
                      const ceramicRating = getRating(feature.ceramic);
                      const DyedIcon = dyedRating.icon;
                      const CeramicIcon = ceramicRating.icon;

                      return (
                        <tr key={index} className="border-b border-gray-800 hover:bg-gray-800/30 transition-colors">
                          <td className="p-6 text-white font-medium">{feature.name}</td>
                          <td className="p-6">
                            <div className="flex items-center justify-center">
                              <div className={`${dyedRating.bg} rounded-full p-2`}>
                                <DyedIcon className={`w-5 h-5 ${dyedRating.color}`} />
                              </div>
                            </div>
                          </td>
                          <td className="p-6">
                            <div className="flex items-center justify-center">
                              <div className={`${ceramicRating.bg} rounded-full p-2`}>
                                <CeramicIcon className={`w-5 h-5 ${ceramicRating.color}`} />
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Legend/Key */}
              <div className="p-6 bg-gray-900/80 border-t border-gray-800">
                <div className="flex flex-wrap justify-center gap-6 text-sm">
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="w-6 h-6 rounded-full bg-green-500/10 flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-500" />
                    </div>
                    <span>Excellent</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="w-6 h-6 rounded-full bg-amber-500/10 flex items-center justify-center">
                      <Check className="w-4 h-4 text-amber-500" />
                    </div>
                    <span>Good</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="w-6 h-6 rounded-full bg-orange-500/10 flex items-center justify-center">
                      <Check className="w-4 h-4 text-orange-500" />
                    </div>
                    <span>Fair / Average</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <div className="w-6 h-6 rounded-full bg-red-500/10 flex items-center justify-center">
                      <X className="w-4 h-4 text-red-500" />
                    </div>
                    <span>Poor / None</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default Comparison;
